import * as React from "react";
import { X, ChevronsUpDown, ChevronRight, GitBranch } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTran } from "@/hooks";
import { getUnitTypeMeta } from "@/features/organization/utils/organization.helpers";
import SelectAllButton from "../ui/SelectAllButton";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import { buildOrgStatusInfoMap } from "./organization.utils";

// Helper to get all descendant IDs for a node
const getAllChildIds = (node) => {
  let ids = [];
  if (node.children && Array.isArray(node.children)) {
    node.children.forEach((child) => {
      const childId = child.id || child._id || child.key;
      if (childId) {
        ids.push(childId);
        ids = [...ids, ...getAllChildIds(child)];
      }
    });
  }
  return ids;
};

const filterTreeKeepGroup = (nodes = [], query = "") => {
  if (!query) return nodes;

  const q = query.toLowerCase();

  const visit = (node) => {
    const nodeText = [node.name, node.code]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const children = Array.isArray(node.children) ? node.children : [];

    if (nodeText.includes(q)) {
      return { ...node, children: [] };
    }

    const matchedChildren = children.map(visit).filter(Boolean);

    if (matchedChildren.length) {
      return { ...node, children: matchedChildren };
    }

    return null;
  };

  return nodes.map(visit).filter(Boolean);
};

const flattenTreeWithDepth = (tree = [], depth = 0, parentChain = []) => {
  const result = [];

  const visit = (nodes, d, parents) => {
    (nodes || []).forEach((node) => {
      const value = node.id || node._id || node.key;
      if (!value) return;

      const fullParentChain = [...parents];

      result.push({
        value,
        label: node.name || value,
        depth: d,
        rawNode: node,
        parentChain: fullParentChain,
      });

      if (Array.isArray(node.children) && node.children.length) {
        visit(node.children, d + 1, [
          ...fullParentChain,
          {
            value,
            label: node.name || value,
            status: node.status,
            inactiveDate: node.inactiveDate,
          },
        ]);
      }
    });
  };

  visit(tree, depth, parentChain);
  return result;
};

const getAllParentNodes = (nodes = []) => {
  const expanded = {};

  const visit = (items) => {
    (items || []).forEach((node) => {
      const value = node.id || node._id || node.key;
      if (node.children?.length) {
        expanded[value] = true;
        visit(node.children);
      }
    });
  };

  visit(nodes);
  return expanded;
};

const BreadcrumbValue = ({
  option,
  onRemove,
  readOnly = false,
  highlighted = false,
  isNewOrgs = false,
  // Map<id, { status, inactiveDate }> — see buildOrgStatusInfoMap
  orgStatusMap = new Map(),
}) => {
  const chain = [
    ...option.parentChain,
    { value: option.value, label: option.label },
  ];

  return (
    <div
      className={cn(
        "flex min-w-0 max-w-full items-center gap-1.5 rounded border px-1.5 py-0.5",
        highlighted
          ? "border-emerald-500 bg-emerald-100 shadow-sm ring-1 ring-emerald-200"
          : "bg-muted/20",
      )}
    >
      {/* Icon: Removed the circle background and top margin for a slimmer profile */}
      <div
        className={cn(
          "flex shrink-0 items-center justify-center",
          highlighted ? "text-emerald-700" : "text-blue-600",
        )}
      >
        <GitBranch className="h-3 w-3" />
      </div>

      {/* Text: Reduced to text-xs and used leading-5 for tighter vertical ratio */}
      <div className="min-w-0 max-w-full flex-1 whitespace-normal break-words text-xs leading-5 flex items-center gap-0.5 flex-wrap">
        {chain.map((item, index) => {
          const isLast = index === chain.length - 1;
          const statusInfo = isLast
            ? {
                status: option.rawNode?.status,
                inactiveDate: option.rawNode?.inactiveDate,
              }
            : {
                status:
                  item.status || orgStatusMap.get(String(item.value))?.status,
                inactiveDate:
                  item.inactiveDate ||
                  orgStatusMap.get(String(item.value))?.inactiveDate,
              };

          return (
            <React.Fragment key={`${option.value}-${item.value}-${index}`}>
              {index > 0 && (
                <span className="px-0.5 text-muted-foreground/50 font-bold">
                  {">"}
                </span>
              )}
              <div className="flex items-center gap-0.5">
                <span
                  className={
                    highlighted && isLast
                      ? "font-bold text-emerald-800"
                      : isLast
                        ? "font-semibold text-blue-600"
                        : "text-muted-foreground"
                  }
                >
                  {item.label}
                </span>
                <OrgStatusTag
                  status={statusInfo.status}
                  inactiveDate={statusInfo.inactiveDate}
                  className="text-[10px]"
                />
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {highlighted || isNewOrgs ? (
        <span className="shrink-0 rounded mb-5 bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none text-white">
          New
        </span>
      ) : null}

      {!readOnly ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onRemove(option.value);
          }}
          className="shrink-0 rounded-sm p-0.5 hover:bg-muted-foreground/10 transition-colors"
        >
          <X className="h-2.5 w-2.5 text-muted-foreground hover:text-foreground" />
        </button>
      ) : null}
    </div>
  );
};

export const OrgTreeNodeBreadcrumb = ({
  value = [],
  onChange,
  tree = [],
  placeholder,
  disabled = false,
  className,
  includeParentCompany = false,
  readOnly = false,
  highlightValues = [],
  isNewOrgs = false,
  allowOrgs = [],
}) => {
  const { t } = useTran();
  const [open, setOpen] = React.useState(false);
  const [viewMode] = React.useState("tree");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [expandedNodes, setExpandedNodes] = React.useState(() =>
    getAllParentNodes(tree),
  );

  const allowOrgSet = React.useMemo(
    () => new Set(allowOrgs.map(String)),
    [allowOrgs],
  );

  const orgStatusMap = React.useMemo(() => buildOrgStatusInfoMap(tree), [tree]);

  const baseTree = React.useMemo(
    () =>
      includeParentCompany ? tree : tree.flatMap((node) => node.children || []),
    [includeParentCompany, tree],
  );

  const filteredTree = React.useMemo(() => {
    if (!searchQuery) return baseTree;
    return filterTreeKeepGroup(baseTree, searchQuery);
  }, [baseTree, searchQuery]);

  const options = React.useMemo(
    () => flattenTreeWithDepth(filteredTree),
    [filteredTree],
  );

  const allOptions = React.useMemo(
    () => flattenTreeWithDepth(baseTree),
    [baseTree],
  );

  const selectedOptions = React.useMemo(
    () => allOptions.filter((option) => value.includes(option.value)),
    [allOptions, value],
  );
  const highlightedValueSet = React.useMemo(
    () => new Set((highlightValues || []).map(String)),
    [highlightValues],
  );

  const handleSelect = (selectedValue) => {
    const newValue = value.includes(selectedValue)
      ? value.filter((item) => item !== selectedValue)
      : [...value, selectedValue];
    onChange(newValue);
  };

  React.useEffect(() => {
    setExpandedNodes(getAllParentNodes(tree));
  }, [tree]);

  React.useEffect(() => {
    if (!searchQuery) return;

    const newExpanded = {};
    const expandParents = (nodes) => {
      (nodes || []).forEach((node) => {
        const nodeValue = node.id || node._id || node.key;
        if (node.children?.length) {
          newExpanded[nodeValue] = true;
          expandParents(node.children);
        }
      });
    };

    expandParents(filteredTree);
    setExpandedNodes(newExpanded);
  }, [searchQuery, filteredTree]);

  if (readOnly) {
    return (
      <div className="flex flex-col gap-1.5 w-full">
        <div
          className={cn(
            "flex max-h-[150px] min-w-0 max-w-full flex-col gap-1 overflow-y-auto rounded-md",
            className,
          )}
        >
          {selectedOptions.length === 0 ? (
            <span className="text-muted-foreground">
              {placeholder || t("Select organizations")}
            </span>
          ) : (
            selectedOptions.map((option) => (
              <BreadcrumbValue
                key={option.value}
                option={option}
                onRemove={handleSelect}
                readOnly
                highlighted={highlightedValueSet.has(String(option.value))}
                isNewOrgs={isNewOrgs}
                orgStatusMap={orgStatusMap}
              />
            ))
          )}
        </div>
        <div className="flex justify-end px-0.5">
          <div className="flex items-center gap-1 text-[11px] font-bold tabular-nums tracking-tight">
            <span className="text-blue-600 bg-blue-50 px-1 rounded-sm border border-blue-100">
              {selectedOptions.length}
            </span>
            <span className="text-muted-foreground/40">/</span>
            <span className="text-muted-foreground">{allOptions.length}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 w-full">
      {/* Header: Label removed, counter pushed to the right */}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "h-auto w-full items-start justify-between overflow-hidden whitespace-normal",
              className,
            )}
            disabled={disabled}
          >
            <div className="flex max-h-[150px] overflow-y-auto min-w-0 max-w-full flex-1 flex-col gap-1 text-left">
              {selectedOptions.length === 0 ? (
                <span className="text-muted-foreground">
                  {placeholder || t("Select organizations")}
                </span>
              ) : (
                selectedOptions.map((option) => (
                  <BreadcrumbValue
                    key={option.value}
                    option={option}
                    onRemove={handleSelect}
                    highlighted={highlightedValueSet.has(String(option.value))}
                    orgStatusMap={orgStatusMap}
                  />
                ))
              )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-full p-0" align="start">
          <div className="border-b p-2">
            <div className="flex items-center rounded-md border px-2">
              <input
                type="text"
                placeholder={t("Search organization...")}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="h-9 w-full bg-transparent text-sm outline-none"
              />
            </div>
          </div>

          <div
            className="max-h-[220px] overflow-y-auto overscroll-contain"
            onWheel={(e) => e.stopPropagation()}
          >
            {options.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {t("No organizations available")}
              </div>
            )}

            {options.map((option) => {
              const depth = option.depth ?? 0;
              const isSelected = value.includes(option.value);
              const meta = getUnitTypeMeta(option?.rawNode?.unitLevel);
              const Icon = meta.icon;
              const isVisible = option.parentChain.every(
                (parent) => expandedNodes[parent.value],
              );

              if (!isVisible) return null;

              // --- Select All Logic ---
              const childIds = getAllChildIds(option.rawNode);

              const allRelatedIds =
                allowOrgSet.size === 0
                  ? [option.value, ...childIds]
                  : [option.value, ...childIds].filter((id) =>
                      allowOrgSet.has(String(id)),
                    );

              const hasChildren = childIds.length > 0;
              const areAllSelected = allRelatedIds.every((id) =>
                value.includes(id),
              );
              const isBusinessLevel = option.rawNode?.unitLevel === "level_1";

              const isAllowed =
                allowOrgSet.size === 0 || allowOrgSet.has(String(option.value));

              return (
                <div key={option.value}>
                  <div
                    className="cursor-pointer rounded-sm transition-colors hover:bg-muted"
                    style={{
                      paddingLeft:
                        viewMode === "tree" ? `${depth * 20 + 10}px` : "10px",
                    }}
                  >
                    <div className="flex min-h-[40px] items-center justify-between px-3 py-2">
                      <div className="mr-3 flex flex-1 items-center gap-2">
                        {viewMode === "tree" &&
                        option.rawNode?.children?.length ? (
                          <div className="flex h-4 w-4 items-center justify-center rounded-full bg-muted">
                            <ChevronRight
                              className={cn(
                                "h-4 w-4 text-muted-foreground transition-transform duration-200",
                                expandedNodes[option.value] ? "rotate-90" : "",
                              )}
                              onClick={(event) => {
                                event.stopPropagation();
                                setExpandedNodes({
                                  ...expandedNodes,
                                  [option.value]: !expandedNodes[option.value],
                                });
                              }}
                            />
                          </div>
                        ) : (
                          <span className="w-4" />
                        )}

                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                          <Icon className={`h-4 w-4 ${meta.color}`} />
                        </div>

                        <div className="flex flex-col gap-0.5">
                          <span className="truncate text-sm">
                            {option.label}
                          </span>
                          <OrgStatusTag
                            status={option.rawNode?.status}
                            inactiveDate={option.rawNode?.inactiveDate}
                            className="text-[10px]"
                          />
                        </div>

                        {isBusinessLevel && hasChildren && (
                          <SelectAllButton
                            className={"ml-auto mr-1"}
                            areAllSelected={areAllSelected}
                            value={value}
                            allRelatedIds={allRelatedIds}
                            onChange={onChange}
                            t={t}
                          />
                        )}
                      </div>

                      {isAllowed && (
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleSelect(option.value)}
                          onClick={(event) => event.stopPropagation()}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      <div className="flex justify-end px-0.5">
        <div className="flex items-center gap-1 text-[11px] font-bold tabular-nums tracking-tight">
          <span className="text-blue-600 bg-blue-50 px-1 rounded-sm border border-blue-100">
            {selectedOptions.length}
          </span>
          <span className="text-muted-foreground/40">/</span>
          <span className="text-muted-foreground">{options.length}</span>
        </div>
      </div>
    </div>
  );
};
